import React from 'react';
import { Pencil } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SharkConfig, CostumeConfig } from '../../../components/SharkAvatarSelector';

interface SharkPreviewCardProps {
  sharkName: string;
  currentShark: SharkConfig;
  currentCostumeObj: CostumeConfig;
  onNameChange: (name: string) => void;
}

export function SharkPreviewCard({
  sharkName,
  currentShark,
  currentCostumeObj,
  onNameChange
}: SharkPreviewCardProps) {
  return (
    <div 
      className="shark-preview-card"
      style={{
        border: '1px solid var(--admin-card-border)',
        borderRadius: '0',
        backgroundColor: 'var(--admin-card-bg)',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        boxSizing: 'border-box'
      }}
    >
      {/* Top Preview Graphic Section (Hidden on Mobile) */}
      <div className="shark-preview-top-section" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--admin-card-subtext)', textTransform: 'uppercase' }}>
          PREVIEW
        </div>

        {/* Front View Frame Box - HEIGHT 210px */}
        <div 
          className="shark-frame-box"
          style={{
            position: 'relative',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            backgroundColor: 'rgba(255, 255, 255, 0.01)',
            borderRadius: '0',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '210px',
            overflow: 'hidden'
          }}
        >
          {/* Dim Blueish Ambient Radial Glow */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at center, rgba(14, 165, 233, 0.08) 0%, rgba(3, 19, 64, 0.04) 60%, transparent 80%)',
            pointerEvents: 'none'
          }} />

          {/* Animated Front-view Graphic */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`front-${currentShark.id}-${currentCostumeObj.id}`}
              initial={{ opacity: 0, scale: 0.88 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.88 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}
            >
              <motion.img
                src={currentCostumeObj.frontView}
                alt={`${currentShark.species} front view`}
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                style={{ height: '140px', width: '140px', objectFit: 'contain', filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.3))' }}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Title + Subtitle */}
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '20px', fontWeight: 700, color: '#ffffff', margin: '0 0 2px' }}>
            {sharkName || currentShark.defaultName}
          </h2>
          <p style={{ fontSize: '11px', color: 'var(--admin-card-subtext)', margin: 0 }}>
            {currentShark.species} · {currentShark.personality}
          </p>
        </div>
      </div>

      {/* Name Your Shark Section (Always Visible on Mobile & Desktop) */}
      <div 
        className="shark-name-section"
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '8px', 
          borderTop: '1px solid rgba(255, 255, 255, 0.05)', 
          paddingTop: '14px', 
          marginTop: '14px' 
        }}
      >
        <label style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--admin-card-subtext)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Pencil size={12} /> NAME YOUR SHARK
        </label>
        <div style={{ position: 'relative', width: '100%' }}>
          <input
            type="text"
            value={sharkName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={currentShark.defaultName}
            maxLength={20}
            style={{
              width: '100%',
              padding: '8px 45px 8px 10px',
              borderRadius: '0',
              border: '1px solid var(--admin-card-border)',
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              color: 'var(--admin-card-text)',
              fontSize: '13px',
              fontFamily: "'Inter', sans-serif",
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '10px', color: 'var(--admin-card-subtext)' }}>
            {sharkName.length}/20
          </span>
        </div>
        <span style={{ fontSize: '10px', color: 'var(--admin-card-subtext)' }}>
          Displayed to your team on every call.
        </span>
      </div>
    </div>
  );
}
